import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Leaderboard.css";
import "../css/EventPage.css";
import useEvents from "../hooks/useEvents";
import useSeason from "../hooks/useSeason";
import { useNavigate } from "react-router-dom";
import SeasonEndLeaderboard from "./SeasonEndLeaderboard";

const Events = ({ fullSize = false }) => {
  const [view, setView] = useState("current");
  const { events, error, loading } = useEvents();
  const { seasons, fetchSeasons, currentSeason, fetchCurrentSeason } =
    useSeason();
  const [season, setSeason] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSeasons();
    fetchCurrentSeason();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setSeason(currentSeason ? currentSeason : null);

    if (currentSeason) {
      const now = new Date();
      const endDate = new Date(currentSeason.end);
      const timeDiff = endDate - now;
      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      setDaysRemaining(days);
    }
  }, [currentSeason]);

  useEffect(() => {
    if (view === "current") {
      setFilteredEvents(
        events.filter(
          (event) => !event.isFinished && event.season === currentSeason?._id
        )
      );
    }
    if (view === "archive") {
      // If a season is selected, filter by season and end date
      if (season) {
        // Filter events that are finished and match the selected season
        const seasonEvents = events.filter(
          (event) => event.isFinished && event.season === season._id
        );

        // Sort by end date (most recent first)
        const sortedEvents = [...seasonEvents].sort((a, b) => {
          // Convert to Date objects for proper comparison
          const dateA = new Date(a.endDate);
          const dateB = new Date(b.endDate);
          // Sort in descending order (newest first)
          return dateB - dateA;
        });

        setFilteredEvents(sortedEvents);
      }
    }

    // Reset to page 1 when changing filters
    setCurrentPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, events, season, currentSeason]);

  // Calculate the current entries to display
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredEvents
    ? filteredEvents.slice(indexOfFirstEntry, indexOfLastEntry)
    : [];

  // Add placeholders to always have 5 entries
  const placeholders = Array.from({
    length: entriesPerPage - currentEntries.length,
  });

  // Calculate total pages
  const totalPages = filteredEvents
    ? Math.ceil(filteredEvents.length / entriesPerPage)
    : 1;

  const formatDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp);
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      className={`main-container ${fullSize ? "" : "col-10 offset-1"}`}
      style={fullSize ? { height: "auto", minHeight: "800px" } : {}}
    >
      <div className="title-container">
        <h1 className="title">Events</h1>
        <div className="view-button-container">
          <button
            className={`view-button ${view === "current" ? "active" : ""}`}
            onClick={() => setView("current")}
          >
            Current
          </button>
          <button
            className={`view-button ${view === "archive" ? "active" : ""}`}
            onClick={() => setView("archive")}
          >
            Archive
          </button>
        </div>
        {view === "archive" && (
          <div className="inventory-box-filter">
            <div className="dropdown">
              <button id="dropdown-button" className="dropdown-button">
                {season && season.name}
              </button>
              <div id="dropdown-content" className="dropdown-content">
                {seasons ? (
                  seasons.map((season, index) => (
                    <p key={index} onClick={() => setSeason(season)}>
                      {season.name}
                    </p>
                  ))
                ) : (
                  <div className="loader" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {view === "current" &&
      filteredEvents &&
      filteredEvents.length === 0 &&
      daysRemaining !== null &&
      daysRemaining <= 30 &&
      daysRemaining >= 0 ? (
        <SeasonEndLeaderboard />
      ) : filteredEvents ? (
        <div className="leaderbord-container">
          <div className="leaderboard-desc-container">
            <h4 className="col-1"> </h4>
            <h4 className="col-2">Event</h4>
            <h4 className="col-2">Start Date</h4>
            <h4 className="col-2">End Date</h4>
            <h4 className="col-2">Fantasy Teams</h4>
            {view === "archive" && <h4 className="col-2">Winner</h4>}
          </div>
          <div className="leaderboard-content">
            {loading && <div className="loader" />}
            {currentEntries.map((item, index) => (
              <div
                className={`leaderboard-entry-container big ${
                  item.isFinished
                    ? ""
                    : !item.isActive
                    ? "isUpcoming"
                    : item.isActive && !item.isLive
                    ? "isActive"
                    : item.isLive === true
                    ? "isLive"
                    : ""
                }`}
                key={index}
                onClick={() => navigate(`/event/${item.hltv_id}`)}
              >
                <div
                  className="col-1"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "0 0.5vw",
                  }}
                >
                  <img className="image" src={item.image} alt={item.name} />
                  <p
                    className={`status-text ${
                      !item.isActive
                        ? "upcoming-text"
                        : item.isActive && !item.isLive
                        ? "active-text"
                        : item.isLive === true
                        ? "live-text"
                        : ""
                    }`}
                  >
                    {item.isFinished
                      ? ""
                      : item.isLive === true
                      ? "Live"
                      : item.isActive
                      ? "Active"
                      : "Upcoming"}
                  </p>
                </div>
                <p className="col-2">{item.name}</p>
                <p className="col-2">{formatDate(item.startDate)}</p>
                <p className="col-2">{formatDate(item.endDate)}</p>
                <p className="col-2">{item.fantasyTeams.length}</p>
                {view === "archive" && (
                  <p className="col-2">{item.winner?.user.username}</p>
                )}
              </div>
            ))}
            {placeholders.map((_, i) => (
              <div
                className="leaderboard-entry-container big placeholder"
                key={`placeholder-${i}`}
              />
            ))}
          </div>
          <div className="pagination">
            <button
              className="default-button"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || totalPages === 0}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="default-button"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      ) : error ? (
        <p className="error">Error fetching events</p>
      ) : (
        <div className="loader" />
      )}
    </div>
  );
};

export default Events;
